import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, CheckCircle2, AlertTriangle, Clock, Download, Plus, Loader2,
} from 'lucide-react';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { Photo } from '../../types';
import { useUploadStore, UploadTask } from '../../store/uploadStore';
import { uploadManager } from '../../services/uploadManager';
import { useLazyUploadThumbnail } from '../../lib/uploadThumbnails';
import styles from './UploadModal.module.css';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
// Extension fallback: Canon .HIF (and some .heic/.heif) files report an empty or
// unrecognized file.type in the browser, so MIME alone would wrongly reject them.
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.hif'];
const ACCEPT_ATTR = [...ALLOWED_TYPES, ...ALLOWED_EXTENSIONS].join(',');

function isAllowedFile(f: File): boolean {
  if (ALLOWED_TYPES.includes(f.type)) return true;
  const name = f.name.toLowerCase();
  return ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── FileRow ─────────────────────────────────────────────────────────

interface FileRowProps {
  task: UploadTask;
  fillRef: (id: string, el: HTMLDivElement | null) => void;
  pctRef: (id: string, el: HTMLSpanElement | null) => void;
}

const FileRow = memo(function FileRow({ task, fillRef, pctRef }: FileRowProps) {
  const [imgError, setImgError] = useState(false);
  const liRef = useLazyUploadThumbnail<HTMLLIElement>(task.id, !!task.previewUrl);
  return (
    <li ref={liRef} className={`${styles['list-item']} ${styles[`item-${task.status}`]}`}>
      <div className={styles['item-thumb']} aria-hidden>
        {task.previewUrl && !imgError ? (
          <img
            src={task.previewUrl}
            alt=""
            className={styles['item-thumb-img']}
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className={styles['item-thumb-fallback']} />
        )}
        <span className={styles['item-thumb-badge']}>
          {task.status === 'queued'    && <Clock         className={styles['icon']} />}
          {task.status === 'uploading' && <Loader2       className={`${styles['icon']} ${styles['spin']}`} />}
          {task.status === 'done'      && <CheckCircle2  className={styles['icon']} />}
          {task.status === 'failed'    && <AlertTriangle className={styles['icon']} />}
        </span>
      </div>
      <div className={styles['item-body']}>
        <div className={styles['item-name-row']}>
          <span className={styles['item-name']} title={task.filename}>{task.filename}</span>
          <span className={styles['item-size']}>{formatBytes(task.size)}</span>
          {task.status === 'uploading' && (
            <span ref={(el) => pctRef(task.id, el)} className={styles['item-pct']}>0%</span>
          )}
          {task.status === 'done' && <span className={styles['item-done-label']}>Done</span>}
        </div>
        {task.status === 'uploading' && (
          <div className={styles['item-track']} aria-hidden>
            <div ref={(el) => fillRef(task.id, el)} className={styles['item-fill']} style={{ width: '0%' }} />
          </div>
        )}
        {task.status === 'failed' && task.error && (
          <span className={styles['item-error']}>{task.error}</span>
        )}
      </div>
    </li>
  );
});

// ── UploadModal ─────────────────────────────────────────────────────

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  onUploaded: (photos: Photo[]) => void;
}

export function UploadModal({ isOpen, onClose, eventId, onUploaded }: UploadModalProps) {
  const allTasks = useUploadStore((s) => s.tasks);
  const isUploading = useUploadStore((s) => s.isUploading);
  const tasks = allTasks.filter((t) => t.eventId === eventId);

  const [dragOver, setDragOver] = React.useState(false);
  const fileInputRef   = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  const progressFillRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const progressPctRefs  = useRef<Map<string, HTMLSpanElement>>(new Map());

  const registerFill = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) progressFillRefs.current.set(id, el);
    else    progressFillRefs.current.delete(id);
  }, []);
  const registerPct = useCallback((id: string, el: HTMLSpanElement | null) => {
    if (el) progressPctRefs.current.set(id, el);
    else    progressPctRefs.current.delete(id);
  }, []);

  // Register per-file progress callback with upload manager
  useEffect(() => {
    if (!isOpen) return;
    return uploadManager.subscribeProgress((id, pct) => {
      const fillEl = progressFillRefs.current.get(id);
      if (fillEl) fillEl.style.width = `${pct}%`;
      const pctEl = progressPctRefs.current.get(id);
      if (pctEl) pctEl.textContent = `${pct}%`;
    });
  }, [isOpen]);

  const addFiles = useCallback((incoming: File[]) => {
    const valid = incoming.filter(isAllowedFile);
    if (valid.length > 0) uploadManager.addFiles(eventId, valid);
  }, [eventId]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    e.target.value = '';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (++dragCounterRef.current === 1) setDragOver(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    if (--dragCounterRef.current === 0) setDragOver(false);
  };
  const handleDragOver  = (e: React.DragEvent) => { e.preventDefault(); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleStartUpload = () => uploadManager.startUpload(onUploaded);

  const doneCount      = tasks.filter((t) => t.status === 'done').length;
  const failedCount    = tasks.filter((t) => t.status === 'failed').length;
  const queuedCount    = tasks.filter((t) => t.status === 'queued').length;
  const uploadingCount = tasks.filter((t) => t.status === 'uploading').length;
  const allSettled     = tasks.length > 0 && queuedCount === 0 && uploadingCount === 0;
  const overallPct     = tasks.length === 0 ? 0 : Math.round((doneCount / tasks.length) * 100);

  const downloadFailureReport = () => {
    const failed = tasks.filter((t) => t.status === 'failed');
    const rows = ['Filename,Error', ...failed.map((t) =>
      `"${t.filename.replace(/"/g, '""')}","${(t.error ?? 'Unknown').replace(/"/g, '""')}"`,
    )];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'upload-failures.csv';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const handleDone = () => {
    uploadManager.clearCompleted(eventId);
    onClose();
  };

  const handleCancel = () => {
    uploadManager.cancelAll();
    onClose();
  };

  // Closing the window (X, backdrop, Escape, or Cancel) discards files that were
  // queued but never started. Active background uploads and settled tasks are
  // left alone — those are handled by the Cancel-while-uploading and Done flows.
  const handleClose = () => {
    if (!isUploading) uploadManager.discardQueued(eventId);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Upload Photos" size="xl">
      <div
        className={styles['container']}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <AnimatePresence>
          {dragOver && (
            <motion.div className={styles['drag-overlay']}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}>
              <Upload className={styles['drag-overlay-icon']} aria-hidden />
              <span className={styles['drag-overlay-text']}>Drop photos here</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className={styles['scroll-area']}>
          {tasks.length === 0 ? (
            <button type="button" className={styles['drop-zone']} onClick={() => fileInputRef.current?.click()}>
              <Upload className={styles['drop-icon']} aria-hidden />
              <span className={styles['drop-title']}>Drag photos here</span>
              <span className={styles['drop-sub']}>or click to browse · JPEG, PNG, WebP, HEIC</span>
            </button>
          ) : (
            <ul className={styles['list']} role="list">
              {tasks.map((t) => (
                <FileRow key={t.id} task={t} fillRef={registerFill} pctRef={registerPct} />
              ))}
            </ul>
          )}
        </div>

        {tasks.length > 0 && (
          <>
            {/* Overall progress bar */}
            <div className={styles['overall-bar']}>
              <div className={styles['overall-track']}>
                <motion.div
                  className={styles['overall-fill']}
                  animate={{ width: `${overallPct}%` }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                />
              </div>
              <span className={styles['overall-label']}>
                {doneCount} of {tasks.length}
              </span>
            </div>

            <div className={styles['footer']}>
              <div className={styles['footer-stats']}>
                {isUploading && (
                  <span className={styles['stat-text']}>
                    Uploading {doneCount + uploadingCount} of {tasks.length - failedCount}…
                  </span>
                )}
                {!isUploading && allSettled && (
                  <span className={styles['stat-text']}>
                    {doneCount > 0 && <span className={styles['stat-done']}>{doneCount} uploaded</span>}
                    {doneCount > 0 && failedCount > 0 && <span className={styles['stat-sep']}> · </span>}
                    {failedCount > 0 && <span className={styles['stat-failed']}>{failedCount} failed</span>}
                  </span>
                )}
                {!isUploading && !allSettled && queuedCount > 0 && (
                  <span className={styles['stat-text']}>
                    {queuedCount} photo{queuedCount !== 1 ? 's' : ''} ready
                  </span>
                )}
              </div>
              <div className={styles['footer-actions']}>
                {!isUploading && (
                  <button type="button" className={styles['add-more-btn']} onClick={() => fileInputRef.current?.click()}>
                    <Plus className={styles['add-more-icon']} aria-hidden />
                    Add more
                  </button>
                )}
                {failedCount > 0 && (
                  <button type="button" className={styles['report-btn']} onClick={downloadFailureReport}>
                    <Download className={styles['report-icon']} aria-hidden />
                    Failure report
                  </button>
                )}
                {isUploading && (
                  <Button variant="ghost" onClick={handleCancel}>Cancel</Button>
                )}
                {!isUploading && !allSettled && queuedCount > 0 && (
                  <Button variant="ghost" onClick={handleClose}>Cancel</Button>
                )}
                {allSettled ? (
                  <Button variant="primary" onClick={handleDone}>Done</Button>
                ) : (
                  <Button variant="primary" onClick={handleStartUpload} loading={isUploading} disabled={queuedCount === 0}>
                    {!isUploading && <Upload className={styles['btn-icon']} aria-hidden />}
                    {isUploading ? 'Uploading…' : `Upload ${queuedCount} photo${queuedCount !== 1 ? 's' : ''}`}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        <input ref={fileInputRef} type="file" accept={ACCEPT_ATTR} multiple
          className={styles['hidden-input']} onChange={handleFileInput} />
      </div>
    </Modal>
  );
}
